import { useCallback, useState } from 'react';
import { bookingWhen, type MyBooking } from './MyBookings';

/**
 * Move a booking to another time (owner 23.10.2026): same therapist and treatment, next 14 days,
 * only times Altegio offers now. The server checks again before it changes anything.
 */
const ERR: Record<string, string> = {
  SLOT_TAKEN: 'This time was just taken — please choose another. · Esta hora acabou de ser ocupada.',
  RESCHEDULE_TOO_LATE:
    'Less than 6 hours before the visit — please message us on WhatsApp. · Menos de 6 h antes — escreva-nos no WhatsApp.',
};
const FALLBACK = 'Something went wrong — please try again. · Algo correu mal — tente de novo.';
const EN_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** The next `count` dates after `from` (YYYY-MM-DD), Sundays left out (studios closed). */
export function nextDates(from: string, count: number): string[] {
  const out: string[] = [];
  const d = new Date(`${from}T12:00:00Z`);
  while (out.length < count) {
    d.setUTCDate(d.getUTCDate() + 1);
    if (d.getUTCDay() !== 0) out.push(d.toISOString().slice(0, 10));
  }
  return out;
}

interface Body {
  data?: { times?: string[]; start?: string };
  error?: { code?: string };
}

export function Reschedule(props: {
  booking: MyBooking;
  token: string;
  fetchImpl: typeof fetch;
  today?: string;
  onDone: (start: string) => void;
  onCancel: () => void;
}) {
  const { booking, token, fetchImpl, onDone, onCancel } = props;
  const [dates] = useState(() => nextDates(props.today ?? new Date().toISOString().slice(0, 10), 14));
  const [date, setDate] = useState<string>();
  const [times, setTimes] = useState<string[] | 'loading'>();
  const [pick, setPick] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const base = `/v1/me/bookings/${encodeURIComponent(booking.id)}`;
  const headers = { Authorization: `Bearer ${token}` };

  const loadTimes = useCallback(
    async (d: string, keepError = false) => {
      setDate(d);
      setPick(undefined);
      if (!keepError) setError(undefined);
      setTimes('loading');
      try {
        const r = await fetchImpl(`${base}/times?date=${d}`, { headers: { Authorization: `Bearer ${token}` } });
        const body = (await r.json().catch(() => ({}))) as Body;
        if (!r.ok) {
          setTimes([]);
          return setError(ERR[body.error?.code ?? ''] ?? FALLBACK);
        }
        setTimes(body.data?.times ?? []);
      } catch {
        setTimes([]);
        setError(FALLBACK);
      }
    },
    [base, fetchImpl, token],
  );

  const move = async () => {
    if (!pick) return;
    setBusy(true);
    setError(undefined);
    try {
      const r = await fetchImpl(`${base}/reschedule`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ start: pick }),
      });
      const body = (await r.json().catch(() => ({}))) as Body;
      if (!r.ok) {
        setBusy(false);
        setError(ERR[body.error?.code ?? ''] ?? FALLBACK);
        if (body.error?.code === 'SLOT_TAKEN' && date) void loadTimes(date, true);
        return;
      }
      onDone(body.data?.start ?? pick);
    } catch {
      setBusy(false);
      setError(FALLBACK);
    }
  };

  const now = bookingWhen(booking.start);
  return (
    <section className="ca-page" aria-label="Change time">
      <h1 className="ca-h1">
        Change time <small>Alterar hora</small>
      </h1>
      <div className="ca-card">
        <div className="ca-muted">Now · Agora</div>
        <b>{now.en}</b>
        <div className="ca-muted">
          {booking.services.join(' + ')}
          {booking.staffName ? ` · ${booking.staffName}` : ''}
        </div>
      </div>
      <div className="ca-days" role="group" aria-label="Day">
        {dates.map((d) => {
          const day = new Date(`${d}T12:00:00Z`);
          return (
            <button key={d} className={d === date ? 'ca-day ca-day-on' : 'ca-day'} onClick={() => void loadTimes(d)}>
              <span>{EN_DAYS[day.getUTCDay()]}</span>
              <b>{day.getUTCDate()}</b>
            </button>
          );
        })}
      </div>
      {times === 'loading' && <p className="ca-hint ca-pad">Loading… · A carregar…</p>}
      {Array.isArray(times) && date && (
        <div className="ca-times" role="group" aria-label="Time">
          {times.length === 0 && !error && (
            <p className="ca-hint">No free time this day. · Sem horas livres neste dia.</p>
          )}
          {times.map((t) => (
            <button
              key={t}
              className={t === pick ? 'ca-time ca-time-on' : 'ca-time'}
              onClick={() => {
                setPick(t);
                setError(undefined);
              }}
            >
              {t.slice(11, 16)}
            </button>
          ))}
        </div>
      )}
      {error && (
        <p className="ca-error ca-pad" role="alert">
          {error}
        </p>
      )}
      <button className="ca-btn ca-btn-red ca-submit" disabled={!pick || busy} onClick={() => void move()}>
        <b>{busy ? 'Moving…' : pick ? `Move to ${bookingWhen(pick).en}` : 'Choose a new time'}</b>
        <span>{pick ? `Mudar para ${bookingWhen(pick).pt}` : 'Escolha a nova hora'}</span>
      </button>
      <div className="ca-row ca-pad">
        <button className="ca-link" onClick={onCancel}>
          ← Back · Voltar
        </button>
      </div>
    </section>
  );
}
